// src/services/csvService.js
import Papa from 'papaparse';
import { firestoreService } from './firestoreService';

class CSVService {
  // Import book from Classmate CSV format
  async importBookFromCSV(file, metadata = {}) {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            console.log('CSV parsed:', results.data);
            
            if (results.errors.length > 0) {
              console.warn('CSV parsing warnings:', results.errors);
            }
            
            const csvData = results.data;
            if (csvData.length === 0) {
              throw new Error('CSV file is empty');
            }
            
            // Create book from first row data
            const firstRow = csvData[0];
            const bookData = this.createBookFromCSVMetadata(firstRow, metadata);
            
            console.log('Creating book with data:', bookData);
            const bookId = await firestoreService.createBook(bookData);
            
            // Convert CSV rows to messages with proper schema mapping
            const messages = this.mapCSVRowsToMessages(csvData, bookId);
            
            console.log('Creating messages:', messages.length);
            await firestoreService.batchCreateMessages(messages);
            
            resolve({
              bookId,
              messageCount: messages.length,
              book: bookData
            });
          } catch (error) {
            console.error('CSV import error:', error);
            reject(error);
          }
        },
        error: (error) => {
          console.error('CSV parse error:', error);
          reject(new Error('Failed to parse CSV file'));
        }
      });
    });
  }

  // Map CSV rows to Firestore message schema (exact mapping)
  mapCSVRowsToMessages(csvRows, bookId) {
    return csvRows.map((row, index) => ({
      // Exact CSV field mapping
      messageId: row.messageId || (index + 1).toString(),
      botId: row.botId || '',
      botName: row.botName || '',
      botCategory: row.botCategory || '',
      type: row.type || 'text',
      condition: row.Condition || '', // Note: Capital C in CSV
      section: row.Section || '',     // Note: Capital S in CSV
      message: row.message || '',
      userResponseType: row.userResponseType || '',
      variable: row.variable || '',
      action: row.Action || '',       // Note: Capital A in CSV
      mediaUrl: row.mediaUrl || '',
      integrationType: row.integrationType || '',
      integrationParameter: row.integrationParameter || '',
      accessLevel: row.AccessLevel || '', // Note: Capital A and L in CSV
      authUsers: row.authUsers || '',
      
      // Add PB-specific fields
      bookId,
      order: index,
      status: 'active',
      createdAt: new Date(),
      updatedAt: new Date(),
      
      // Preserve any additional CSV columns
      additionalData: this.extractAdditionalData(row)
    }));
  }

  // Create book metadata from CSV data
  createBookFromCSVMetadata(firstRow, metadata) {
    return {
      // Basic book info from CSV
      botId: firstRow.botId || `book_${Date.now()}`,
      botName: firstRow.botName || 'Imported Book',
      botCategory: firstRow.botCategory || 'General',
      botTask: firstRow.botTask || 'Interactive book imported from CSV',
      
      // Template and access settings
      templateType: metadata.templateType || this.detectTemplateType(firstRow),
      accessLevel: metadata.accessLevel || 'private',
      published: metadata.published || false,
      
      // Owner and timestamps
      ownerId: metadata.ownerId,
      createdAt: new Date(),
      updatedAt: new Date(),
      
      // Custom styling from metadata
      customStyling: this.extractCustomStyling(metadata),
      
      // Analytics
      analytics: {
        views: 0,
        interactions: 0,
        completions: 0
      }
    };
  }

  // Auto-detect template type from CSV content
  detectTemplateType(firstRow, metadata) {
    if (metadata.templateType) return metadata.templateType;
    
    const category = (firstRow.botCategory || firstRow.pageCategory || '').toLowerCase();
    const name = (firstRow.botName || firstRow.pageName || '').toLowerCase();
    
    // Check for catalog/product-specific headers
    const headers = Object.keys(firstRow).map(h => h.toLowerCase());
    const catalogHeaders = ['price', 'brand', 'imageurl', 'specs', 'category'];
    const hasCatalogHeaders = catalogHeaders.some(header => 
      headers.some(h => h.includes(header))
    );
    
    if (hasCatalogHeaders || category.includes('catalog') || category.includes('product') || category.includes('store')) {
      return 'catalog';
    }
    
    // Auto-detect based on content
    if (category.includes('education') || name.includes('grade') || name.includes('math')) {
      return 'education';
    } else if (category.includes('marketing') || category.includes('campaign')) {
      return 'marketing';
    } else if (category.includes('brochure')) {
      return 'brochure';
    } else if (category.includes('album') || category.includes('gallery')) {
      return 'album';
    }
    
    return 'general'; // Default template
  }

  // Extract custom styling from metadata
  extractCustomStyling(metadata) {
    return {
      primaryColor: metadata.primaryColor || '#0795ff',
      backgroundColor: metadata.backgroundColor || '#f6f5f5',
      headerColor: metadata.headerColor || '#0795ff',
      customCSS: metadata.customCSS || '',
      favicon: metadata.favicon || ''
    };
  }

  // Preserve additional CSV data not in standard schema
  extractAdditionalData(row) {
    const standardFields = [
      'messageId', 'botId', 'botName', 'botCategory', 'type', 
      'Condition', 'Section', 'message', 'userResponseType', 
      'variable', 'Action', 'mediaUrl', 'integrationType', 
      'integrationParameter', 'AccessLevel', 'authUsers'
    ];
    
    const additional = {};
    Object.keys(row).forEach(key => {
      if (!standardFields.includes(key) && row[key] && row[key].trim() !== '') {
        additional[key] = row[key];
      }
    });
    
    return additional;
  }

  // Export book back to CSV format
  async exportBookToCSV(bookId) {
    const book = await firestoreService.getBook(bookId);
    const messages = await firestoreService.getBookMessages(bookId);
    
    // Convert back to original CSV format
    const csvData = messages.map(msg => ({
      messageId: msg.messageId,
      botId: msg.botId,
      botName: msg.botName,
      botCategory: msg.botCategory,
      type: msg.type,
      Condition: msg.condition,        // Restore capital C
      Section: msg.section,            // Restore capital S
      message: msg.message,
      userResponseType: msg.userResponseType,
      variable: msg.variable,
      Action: msg.action,              // Restore capital A
      mediaUrl: msg.mediaUrl,
      integrationType: msg.integrationType,
      integrationParameter: msg.integrationParameter,
      AccessLevel: msg.accessLevel,    // Restore capitals
      authUsers: msg.authUsers,
      '': '' // Empty column if needed
    }));

    return Papa.unparse(csvData);
  }

  // Generate template messages for new books
  getTemplateMessages(template, bookId, bookData) {
    const templates = {
      education: [
        {
          messageId: '1',
          botId: bookData.botId,
          botName: bookData.botName,
          botCategory: 'Education',
          type: 'text',
          condition: '',
          section: 'Introduction',
          message: 'Welcome to your interactive learning experience!',
          userResponseType: '',
          variable: '',
          action: '',
          mediaUrl: '',
          integrationType: '',
          integrationParameter: '',
          accessLevel: '',
          authUsers: '',
          bookId,
          order: 0,
          status: 'active',
          createdAt: new Date()
        }
      ],
      marketing: [
        {
          messageId: '1',
          botId: bookData.botId,
          botName: bookData.botName,
          botCategory: 'Marketing',
          type: 'text',
          condition: '',
          section: 'Welcome',
          message: 'Discover our amazing products and services!',
          userResponseType: '',
          variable: '',
          action: '',
          mediaUrl: '',
          integrationType: '',
          integrationParameter: '',
          accessLevel: '',
          authUsers: '',
          bookId,
          order: 0,
          status: 'active',
          createdAt: new Date()
        }
      ],
      general: [
        {
          messageId: '1',
          botId: bookData.botId,
          botName: bookData.botName,
          botCategory: 'General',
          type: 'text',
          condition: '',
          section: 'Start',
          message: 'Welcome to your interactive book!',
          userResponseType: '',
          variable: '',
          action: '',
          mediaUrl: '',
          integrationType: '',
          integrationParameter: '',
          accessLevel: '',
          authUsers: '',
          bookId,
          order: 0,
          status: 'active',
          createdAt: new Date()
        }
      ]
    };

    return templates[template.id] || templates.general;
  }
}

export const csvService = new CSVService();